import {
  COMMENT_JOB_TRANSITIONS,
  approveCommentJobResponseSchema,
  cancelCommentJobResponseSchema,
  commentJobDetailResponseSchema,
  commentJobListResponseSchema,
  generateCommentResponseSchema,
  type ApproveCommentJobRequest,
  type ApproveCommentJobResponse,
  type CancelCommentJobRequest,
  type CancelCommentJobResponse,
  type CommentJobDetailResponse,
  type CommentJobListItem,
  type CommentJobListQuery,
  type CommentJobStatus,
  type CommentJobUpdated,
  type GenerateCommentResponse,
  type PaginationMeta,
  type generateCommentRequestSchema,
} from '@midiautolistening/contracts';
import { defineStore } from 'pinia';
import { ref, type Ref } from 'vue';
import type { z } from 'zod';

import { apiGet, apiPost, toApiError, type ApiRequestError } from '@/lib/api';
import type { PostMatchId } from '@/lib/post-identity';

/**
 * The comment half of the platform: drafting a candidate, the job queue that
 * carries it, and the two decisions an operator can make about one.
 *
 * Three things this store is careful about:
 *
 *  - **Generation addresses a post by its match id.** `POST
 *    /posts/:postMatchId/generate-comment` is a project-scoped route, and the
 *    branded `PostMatchId` is what keeps a global `post.id` from being handed to
 *    it — the mistake that answered 404 on every post action once already.
 *  - **A write re-reads rather than patches.** Approving does not simply stamp
 *    the local row APPROVED: the server may hold the job back on a rate limit,
 *    or dispatch it and move it two states on. Only the server knows which, so
 *    the detail and the list are re-read from it.
 *  - **Busy state is per job id.** The approval queue shows many rows at once
 *    and a single boolean would spin every button on the page.
 */

/**
 * The request body as a *caller* writes it.
 *
 * `z.input`, not the exported `GenerateCommentRequest`: `source`, `style` and
 * `includeLink` carry `.default()`, so the inferred output type marks them
 * required while the wire format does not. Typing callers against the output
 * would force every screen to restate the same three defaults.
 */
export type GenerateCommentInput = z.input<typeof generateCommentRequestSchema>;

/** Every `GET /comment-jobs` filter, all optional; page defaults applied below. */
export type CommentJobFilters = Partial<CommentJobListQuery>;

const DEFAULT_PAGE_SIZE = 25;

/**
 * Would the page currently on screen still contain a row in this status?
 *
 * Only two filters are consulted, and that is a decision rather than a shortcut:
 * `status` and `awaitingApproval` are the only ones a status change can move a
 * row across. `projectId`, `postId` and `source` do not change over a job's life,
 * and re-deriving the whole `GET /comment-jobs` where-clause on the client is the
 * class of duplication that drifts the first time a filter is added server-side.
 * Anything this cannot decide simply leaves the list marked stale, which is the
 * safe answer.
 */
function matchesStatusFilters(status: CommentJobStatus, query: CommentJobFilters): boolean {
  if (query.awaitingApproval === true && status !== 'WAITING_APPROVAL') return false;
  const wanted = query.status;
  if (wanted === undefined) return true;
  return Array.isArray(wanted) ? wanted.includes(status) : wanted === status;
}

/**
 * What is left of the review-signal bridge.
 *
 * This store used to parse both comment reads through locally widened copies of
 * `commentJobDetailResponseSchema` and `generateCommentResponseSchema`, because
 * a zod object strips what it does not declare and `commentCandidateSchema` did
 * not yet declare `reviewFlags` / `unsupportedClaims` — so the two fields that
 * name what is wrong with a draft would have been dropped on the floor between
 * the response body and the screen. The contract carries them now, required, so
 * the widened copies are gone and the contract schemas are used directly.
 *
 * The two names survive as aliases only so the comment screens keep compiling
 * this round; they are the contract types exactly, and new code should say
 * `CommentJobDetailResponse` / `GenerateCommentResponse`.
 */
export type ReviewedJobDetailResponse = CommentJobDetailResponse;
export type ReviewedGenerateResponse = GenerateCommentResponse;

export const useCommentsStore = defineStore('comments', () => {
  const jobs = ref<CommentJobListItem[]>([]);
  const jobsMeta = ref<PaginationMeta | null>(null);
  const jobsLoading = ref(false);
  const jobsError = ref<ApiRequestError | null>(null);
  /** The filters the visible page was fetched with, so a refresh reproduces it. */
  const jobsQuery = ref<CommentJobFilters>({});
  /**
   * A realtime event changed something this page cannot show correctly.
   *
   * Set when a `comment.job_updated` names a job that is not on this page (a job
   * somebody else just created), or when a patched row no longer matches the
   * filter it was fetched under (an approved job on the "chờ duyệt" tab). The row
   * is NOT removed in that second case: making a job vanish while somebody is
   * reading it is worse than showing it with an honest chip. The screen offers a
   * refresh instead.
   */
  const jobsStale = ref(false);

  const detail = ref<ReviewedJobDetailResponse | null>(null);
  const detailLoading = ref(false);
  const detailError = ref<ApiRequestError | null>(null);

  const generating = ref(false);
  const generateError = ref<ApiRequestError | null>(null);

  const approving = ref<Record<string, boolean>>({});
  const cancelling = ref<Record<string, boolean>>({});
  /** The last approve/cancel failure — a 422 POLICY_REJECTED lands here. */
  const actionError = ref<ApiRequestError | null>(null);

  function markInFlight(
    flags: Ref<Record<string, boolean>>,
    jobId: string,
    busy: boolean,
  ): void {
    if (busy) {
      flags.value = { ...flags.value, [jobId]: true };
      return;
    }
    const next = { ...flags.value };
    delete next[jobId];
    flags.value = next;
  }

  function isApproving(jobId: string): boolean {
    return approving.value[jobId] === true;
  }

  function isCancelling(jobId: string): boolean {
    return cancelling.value[jobId] === true;
  }

  /**
   * Draft a comment for one post.
   *
   * Returns the whole response because three separate facts live in it and no
   * two of them are the same question. `candidate.validation` explains a refusal
   * — a normal outcome here, not an error: a refused body still comes back 200
   * and the screen shows why rather than pretending the request broke.
   * `commandStatus` says whether an automatic project already sent this, and
   * `jobId` says nothing a caller should branch on by itself.
   *
   * `describeGenerateOutcome` in `lib/comment-view` is where those three become
   * one sentence. A screen that reads `jobId !== null` and writes its own is how
   * "Bình luận đã được gửi đi ngay" came to fire on an offline agent.
   */
  async function generate(
    postMatchId: PostMatchId,
    request: GenerateCommentInput,
  ): Promise<ReviewedGenerateResponse> {
    generating.value = true;
    generateError.value = null;
    try {
      return await apiPost(
        `/posts/${postMatchId}/generate-comment`,
        generateCommentResponseSchema,
        request,
      );
    } catch (caught) {
      const failure = toApiError(caught);
      generateError.value = failure;
      throw failure;
    } finally {
      generating.value = false;
    }
  }

  async function fetchJobs(query: CommentJobFilters = {}): Promise<void> {
    jobsQuery.value = query;
    jobsLoading.value = true;
    jobsError.value = null;
    try {
      const response = await apiGet('/comment-jobs', commentJobListResponseSchema, {
        page: query.page ?? 1,
        pageSize: query.pageSize ?? DEFAULT_PAGE_SIZE,
        projectId: query.projectId,
        postId: query.postId,
        // Repeated keys for an array — `serializeParams` in lib/api handles it.
        status: query.status,
        source: query.source,
        browserProfileId: query.browserProfileId,
        from: query.from,
        to: query.to,
        awaitingApproval: query.awaitingApproval,
        sortOrder: query.sortOrder ?? 'desc',
      });
      jobs.value = response.items;
      jobsMeta.value = response.meta;
      jobsStale.value = false;
    } catch (caught) {
      jobsError.value = toApiError(caught);
      jobs.value = [];
      jobsMeta.value = null;
    } finally {
      jobsLoading.value = false;
    }
  }

  async function fetchJob(jobId: string): Promise<void> {
    detailLoading.value = true;
    detailError.value = null;
    try {
      detail.value = await apiGet(`/comment-jobs/${jobId}`, commentJobDetailResponseSchema);
    } catch (caught) {
      detailError.value = toApiError(caught);
      detail.value = null;
    } finally {
      detailLoading.value = false;
    }
  }

  /**
   * Re-read the open job without touching `detailLoading`.
   *
   * A `comment.job_updated` carries a status and nothing else — no `results`, no
   * `commandId`, no `approvedAt` — so the patch below makes the panel honest
   * about *what state the job is in* and cannot fill in the link to a comment
   * that was just published. This read finishes the job.
   *
   * Quiet on purpose: `fetchJob` flips `detailLoading`, and an event arriving
   * while somebody is reading a draft would blank the panel out from under them.
   * A failure is swallowed for the same reason — the patch has already corrected
   * the one thing that was actively lying, and a toast about a background read
   * teaches people to ignore toasts.
   */
  async function refreshDetailQuietly(jobId: string): Promise<void> {
    try {
      const fresh = await apiGet(`/comment-jobs/${jobId}`, commentJobDetailResponseSchema);
      // The operator may have opened another job while this was in flight.
      if (detail.value?.job.id === jobId) detail.value = fresh;
    } catch {
      // Deliberately silent; see above.
    }
  }

  /**
   * Apply a `comment.job_updated` frame.
   *
   * THE BUG THIS FIXES. `ws.ts` parsed this event and threw it away under a
   * comment reading "Not surfaced on any MVP screen", which stopped being true
   * the day `CommentJobsView` shipped. The API broadcasts on every transition;
   * the queue polls nothing. So a job somebody else had already approved — or one
   * the agent had already typed onto a stranger's post — went on showing the chip
   * "Chờ bạn duyệt" with a live "Duyệt & gửi" beside it. Pressing it answered 409
   * at best, and at worst asked for a second comment on a post that already had
   * one.
   *
   * `allowedTransitions` is rebuilt from `COMMENT_JOB_TRANSITIONS` rather than
   * left alone, and that matters more than the chip: it is what `canApprove` and
   * `canCancel` read on the detail panel, and a stale copy of it is precisely a
   * live button over a job that has moved on. The table is the same object the
   * API answers from, so this is not a second opinion — it is the same one.
   */
  function applyJobUpdated(event: CommentJobUpdated): void {
    const index = jobs.value.findIndex((row) => row.id === event.commentJobId);

    if (index === -1) {
      // Nothing to patch. A list that has been loaded is now missing a change,
      // which is only worth reporting if there is a list on screen at all.
      if (jobsMeta.value !== null) jobsStale.value = true;
    } else {
      const next = [...jobs.value];
      const row = next[index];
      if (row !== undefined) {
        next[index] = {
          ...row,
          status: event.status,
          rejectionReason: event.rejectionReason,
          errorMessage: event.errorMessage,
          updatedAt: event.occurredAt,
        };
        jobs.value = next;
      }
      if (!matchesStatusFilters(event.status, jobsQuery.value)) jobsStale.value = true;
    }

    const open = detail.value;
    if (open === null || open.job.id !== event.commentJobId) return;

    detail.value = {
      ...open,
      job: {
        ...open.job,
        status: event.status,
        rejectionReason: event.rejectionReason,
        errorMessage: event.errorMessage,
        updatedAt: event.occurredAt,
      },
      allowedTransitions: [...COMMENT_JOB_TRANSITIONS[event.status]],
    };

    // Only when the status actually moved: a burst of events for one job must not
    // become a burst of reads.
    if (open.job.status !== event.status) void refreshDetailQuietly(event.commentJobId);
  }

  /**
   * Re-read whatever this action could have changed.
   *
   * The detail is only re-read when it is the job on screen: approving from a
   * list row must not swap the open panel out from under the operator. Neither
   * read can throw — both park their own failure in their own error ref — so a
   * refresh that fails cannot mask the fact that the action itself succeeded.
   */
  async function refreshAfterAction(jobId: string): Promise<void> {
    const reads: Promise<void>[] = [];
    if (detail.value?.job.id === jobId) reads.push(fetchJob(jobId));
    if (jobsMeta.value !== null) reads.push(fetchJobs(jobsQuery.value));
    await Promise.all(reads);
  }

  /**
   * Approve and dispatch.
   *
   * Rethrows: the caller has to know. A `422 POLICY_REJECTED` here means nothing
   * was sent and the job is still sitting in WAITING_APPROVAL — so that operator
   * will have to approve it again, which is the opposite of what the screens used
   * to say.
   *
   * A *successful* call is not the same as a comment on its way either: the
   * response carries `commandStatus: 'PENDING'` when the agent was offline, and
   * that command never touched the socket. "Đã duyệt", "đã gửi đi" and "đã đăng"
   * are three different facts; `describeApproveOutcome` in `lib/comment-view` is
   * where the response is turned into the right one, so no screen has to invent
   * the sentence itself.
   */
  async function approve(
    jobId: string,
    body: ApproveCommentJobRequest = {},
  ): Promise<ApproveCommentJobResponse> {
    markInFlight(approving, jobId, true);
    actionError.value = null;
    try {
      const response = await apiPost(
        `/comment-jobs/${jobId}/approve`,
        approveCommentJobResponseSchema,
        body,
      );
      await refreshAfterAction(jobId);
      return response;
    } catch (caught) {
      const failure = toApiError(caught);
      actionError.value = failure;
      throw failure;
    } finally {
      markInFlight(approving, jobId, false);
    }
  }

  /**
   * Cancel a job — which is not the same as stopping a comment.
   *
   * The row always moves to CANCELLED; whether anything was actually recalled is
   * a second question the response now answers separately, in `recallOutcome`
   * and `guaranteedNotPosted`. `COMMENT_JOB_TRANSITIONS` allows
   * SENT_TO_AGENT → CANCELLED, so this call is live while the `comment.execute`
   * frame is already on the operator's own machine and the agent may be typing
   * into Facebook right then.
   *
   * The response is returned rather than swallowed for exactly that reason, and
   * `describeCancelOutcome` in `lib/comment-view` turns it into the sentence.
   * Both screens used to answer this call with a green "Đã huỷ job" built from
   * `response.status` alone — true about the row, false about the comment.
   */
  async function cancel(
    jobId: string,
    body: CancelCommentJobRequest = {},
  ): Promise<CancelCommentJobResponse> {
    markInFlight(cancelling, jobId, true);
    actionError.value = null;
    try {
      const response = await apiPost(
        `/comment-jobs/${jobId}/cancel`,
        cancelCommentJobResponseSchema,
        body,
      );
      await refreshAfterAction(jobId);
      return response;
    } catch (caught) {
      const failure = toApiError(caught);
      actionError.value = failure;
      throw failure;
    } finally {
      markInFlight(cancelling, jobId, false);
    }
  }

  function reset(): void {
    jobs.value = [];
    jobsMeta.value = null;
    jobsError.value = null;
    jobsQuery.value = {};
    jobsStale.value = false;
    detail.value = null;
    detailError.value = null;
    generateError.value = null;
    actionError.value = null;
    approving.value = {};
    cancelling.value = {};
  }

  return {
    jobs,
    jobsMeta,
    jobsLoading,
    jobsError,
    jobsQuery,
    jobsStale,
    detail,
    detailLoading,
    detailError,
    generating,
    generateError,
    approving,
    cancelling,
    actionError,
    isApproving,
    isCancelling,
    generate,
    fetchJobs,
    fetchJob,
    approve,
    cancel,
    applyJobUpdated,
    reset,
  };
});
